import type { GpsPosition } from '../sensors/gps';

interface TrackPoint {
  lat: number;
  lon: number;
  time: Date;
  elevation?: number;
  speed?: number;
}

export class GpxRecorder {
  private isRecording: boolean = false;
  private trackPoints: TrackPoint[] = [];
  private lastPoint: TrackPoint | null = null;
  private readonly minDistance: number = 4; // mètres
  private startTime: Date | null = null;

  constructor() {}

  start(): void {
    if (this.isRecording) return;
    
    this.isRecording = true;
    this.trackPoints = [];
    this.lastPoint = null;
    this.startTime = new Date();
    console.log('🟢 Enregistrement GPX démarré');
  }

  stop(): void {
    if (!this.isRecording) return;
    
    this.isRecording = false;
    console.log(`🛑 Enregistrement GPX arrêté - ${this.trackPoints.length} points enregistrés`);
  }

  addPosition(position: GpsPosition): boolean {
    if (!this.isRecording) return false;
    
    const newPoint: TrackPoint = {
      lat: position.lat,
      lon: position.lon,
      time: new Date(),
      elevation: undefined, // Pas d'altitude dans GpsPosition
      speed: position.speed || undefined
    };

    // Vérifier la distance par rapport au point précédent
    if (this.lastPoint && this.calculateDistance(this.lastPoint, newPoint) < this.minDistance) {
      return false; // Point ignoré car trop proche
    }

    this.trackPoints.push(newPoint);
    this.lastPoint = newPoint;
    
    console.log(`📍 Point GPX ajouté: ${position.lat.toFixed(6)}, ${position.lon.toFixed(6)} (total: ${this.trackPoints.length})`);
    return true;
  }

  private calculateDistance(point1: TrackPoint, point2: TrackPoint): number {
    const R = 6371000; // Rayon de la Terre en mètres
    const dLat = this.toRadians(point2.lat - point1.lat);
    const dLon = this.toRadians(point2.lon - point1.lon);
    
    const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
              Math.cos(this.toRadians(point1.lat)) * Math.cos(this.toRadians(point2.lat)) *
              Math.sin(dLon / 2) * Math.sin(dLon / 2);
    
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  }

  private toRadians(degrees: number): number {
    return degrees * (Math.PI / 180);
  }

  generateGPX(): string {
    if (this.trackPoints.length === 0) {
      throw new Error('Aucun point enregistré');
    }

    const trackName = `Traktion GPS ${this.startTime?.toISOString().split('T')[0] || 'Unknown'}`;
    const creator = 'Traktion PWA';
    
    const trackPointsXml = this.trackPoints.map(point => {
      const elevationAttr = point.elevation !== undefined ? ` ele="${point.elevation.toFixed(1)}"` : '';
      const speedAttr = point.speed !== undefined ? `    <speed>${point.speed.toFixed(2)}</speed>\n` : '';
      
      return `    <trkpt lat="${point.lat.toFixed(7)}" lon="${point.lon.toFixed(7)}"${elevationAttr}>
      <time>${point.time.toISOString()}</time>
${speedAttr}    </trkpt>`;
    }).join('\n');

    return `<?xml version="1.0" encoding="UTF-8"?>
<gpx version="1.1" creator="${creator}" xmlns="http://www.topografix.com/GPX/1/1">
  <metadata>
    <name>${trackName}</name>
    <time>${this.startTime?.toISOString() || new Date().toISOString()}</time>
  </metadata>
  <trk>
    <name>${trackName}</name>
    <trkseg>
${trackPointsXml}
    </trkseg>
  </trk>
</gpx>`;
  }

  downloadGPX(): void {
    try {
      const gpxContent = this.generateGPX();
      const timestamp = this.startTime?.toISOString().replace(/[:.]/g, '-').slice(0, 19) || 'unknown';
      const filename = `traktion-gps-${timestamp}.gpx`;
      
      const blob = new Blob([gpxContent], { type: 'application/gpx+xml' });
      const url = URL.createObjectURL(blob);
      
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.click();
      
      URL.revokeObjectURL(url);
      console.log(`💾 GPX téléchargé: ${filename}`);
      
    } catch (error) {
      console.error('❌ Erreur génération GPX:', error);
      throw error;
    }
  }

  getStats(): { pointCount: number; isRecording: boolean; duration: string } {
    const duration = this.startTime ? 
      Math.floor((Date.now() - this.startTime.getTime()) / 1000) : 0;
    
    const formatDuration = (seconds: number): string => {
      const hours = Math.floor(seconds / 3600);
      const minutes = Math.floor((seconds % 3600) / 60);
      const secs = seconds % 60;
      return hours > 0 ? `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}` 
                       : `${minutes}:${secs.toString().padStart(2, '0')}`;
    };

    return {
      pointCount: this.trackPoints.length,
      isRecording: this.isRecording,
      duration: formatDuration(duration)
    };
  }

  clear(): void {
    this.trackPoints = [];
    this.lastPoint = null;
    this.startTime = null;
    console.log('🗑️ Enregistrement GPX effacé');
  }
}

// Instance singleton
export const gpxRecorder = new GpxRecorder();